import React, { FC, useEffect, useMemo, useState } from 'react';
import DayViewNavigation from './DayViewNavigation/DayViewNavigation';

import useAppointmentDetail from 'hooks/useAppointmentDetail';
import usePractitionerWeeklyTimeSlot, {
  IAvailableBlocks,
} from 'hooks/usePractitionerWeeklyTimeSlot';

import moment from 'moment';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import DayView from '../DayView/DayView';
import MonthView from '../MonthView/MonthView';
import { SelectedTimeSlot } from 'components/DuplicateAppointment/DupplicateAppointment';
import { sortBy } from 'lodash';
import MonthViewNavigation from 'components/MonthViewNavigation/MonthViewNavigation';
import getTimeSlotPerInterval from 'utils/getTimeSlotsPerInterval';
import useClinicWeeklyTimeSlot from 'hooks/useClinicWeeklyTimeSlot';
import { mergePractitionersAvaiBlocks } from './utils/mergePractitionerSlot';
import { AMOUNT_DAYS_OF_A_MONTH } from 'utils/constants';

interface BookNowPanelProps {
  appointmentId: string;
  practitionerId: string | null;
  selectedTimeSlot: SelectedTimeSlot;
  setSelectedTimeSlot: React.Dispatch<React.SetStateAction<SelectedTimeSlot>>;
  updateAvailableBlocksRef: (data: IAvailableBlocks[]) => void;
  isOutsideHourMode: boolean;
  slotInterval: number;
}

const BookNowPanel: FC<BookNowPanelProps> = ({
  practitionerId,
  appointmentId,
  selectedTimeSlot,
  setSelectedTimeSlot,
  updateAvailableBlocksRef,
  isOutsideHourMode,
  slotInterval,
}) => {
  const [isDayView, setIsDayView] = useState(true);
  const { data: appointmentDetail } = useAppointmentDetail(appointmentId);

  const duplicateAppointmentDate = appointmentDetail.appointmentDate;

  const [date, setDate] = useState(duplicateAppointmentDate);

  const currentDate = moment.tz(appointmentDetail.timezone).format();

  const endDate = moment(date).add('4', 'day').format('YYYY-MM-DD');

  const isSelectedPractitioner = !!practitionerId;

  const {
    data: practitionerWeeklyTimeSlot,
    isLoading: isPractitionerWeeklyTimeSlotLoading,
  } = usePractitionerWeeklyTimeSlot({
    shouldRun: isSelectedPractitioner,
    practitionerId: practitionerId!,
    serviceId: appointmentDetail.service.id,
    currentDate,
    dates: [date, endDate],
    timeBlocks: [0],
    timezone: appointmentDetail.timezone,
  });

  const {
    data: clinicWeeklyTimeSlot,
    isLoading: isClinicWeeklyTimeSlotLoading,
  } = useClinicWeeklyTimeSlot({
    shouldRun: !isSelectedPractitioner,
    serviceId: appointmentDetail.service.id,
    currentDate,
    dates: [date, endDate],
    timeBlocks: [0],
    timezone: appointmentDetail.timezone,
  });

  const isDataLoading = practitionerId
    ? isPractitionerWeeklyTimeSlotLoading
    : isClinicWeeklyTimeSlotLoading;

  //--For selected practitioner--
  const updatedAvailableBlocksBySelectedPractitioner = useMemo(() => {
    if (isDataLoading || !isSelectedPractitioner) {
      return [
        {
          id: practitionerId,
          availableBlocks: [],
        },
      ];
    }

    const availableBlocks = getTimeSlotPerInterval({
      availableBlocks: practitionerWeeklyTimeSlot?.[0]?.clinic?.availableBlocks,
      slotInterval,
    });

    const sortedAvailableBlocks = availableBlocks.map((block: any) => {
      let timeBlocks = [...block.blocks];
      timeBlocks = sortBy(timeBlocks);

      return { ...block, blocks: timeBlocks };
    });

    return [
      {
        id: practitionerId,
        availableBlocks: sortedAvailableBlocks,
      },
    ];
  }, [
    isDataLoading,
    isSelectedPractitioner,
    practitionerWeeklyTimeSlot,
    slotInterval,
    practitionerId,
  ]);

  //--For no preference--
  const doctorWeeklyTimeSlot = useMemo(() => {
    if (isDataLoading || isSelectedPractitioner) {
      return [];
    }

    const result =
      clinicWeeklyTimeSlot?.[0]?.doctors?.map((doctor: any) => {
        const availableBlocks = getTimeSlotPerInterval({
          availableBlocks: doctor.availableBlocks,
          slotInterval,
        });

        const sortedAvailableBlocks = availableBlocks.map((block: any) => {
          let timeBlocks = [...block.blocks];
          timeBlocks = sortBy(timeBlocks);

          return { ...block, blocks: timeBlocks };
        });

        return {
          ...doctor,
          availableBlocks: sortedAvailableBlocks,
        };
      }) || [];

    return result;
  }, [
    clinicWeeklyTimeSlot,
    isDataLoading,
    isSelectedPractitioner,
    slotInterval,
  ]);

  const mergePractitionerSlotByAllPractitioners = useMemo(() => {
    const result = mergePractitionersAvaiBlocks(
      doctorWeeklyTimeSlot,
      slotInterval
    );

    return result;
  }, [doctorWeeklyTimeSlot, slotInterval]);

  useEffect(() => {
    setSelectedTimeSlot(null);
  }, [practitionerId, setSelectedTimeSlot]);

  useEffect(() => {
    if (selectedTimeSlot) return;

    //--Selected doctor--
    if (isSelectedPractitioner) {
      const tmpBlockByDuplicateDate: number[] =
        updatedAvailableBlocksBySelectedPractitioner[0]?.availableBlocks.find(
          (item) => item.date === duplicateAppointmentDate
        )?.blocks || [];

      if (tmpBlockByDuplicateDate.length !== 0) {
        setSelectedTimeSlot({
          date: duplicateAppointmentDate,
          value: tmpBlockByDuplicateDate[0],
          practitionerId,
        });
      } else {
        const nearestTmpBlocks =
          updatedAvailableBlocksBySelectedPractitioner[0]?.availableBlocks.find(
            (item) => item.blocks.length !== 0
          ) || [];

        if (nearestTmpBlocks.length !== 0) {
          setSelectedTimeSlot({
            date: nearestTmpBlocks.date,
            value: nearestTmpBlocks?.blocks[0],
            practitionerId,
          });
        }
      }
    }

    //--No preference--
    if (!isSelectedPractitioner) {
      const dateItems = mergePractitionerSlotByAllPractitioners.dateItems;

      const dates = Object.keys(dateItems);

      for (const date of dates) {
        const blockNumbers = Object.keys(dateItems[date]);

        if (blockNumbers.length !== 0) {
          const blockNumberKey = blockNumbers[0];

          setSelectedTimeSlot({
            date,
            value: parseInt(blockNumberKey, 10),
            practitionerId: dateItems[date][blockNumberKey].practitionerIds[0],
          });
          break;
        }
      }
    }
  }, [
    duplicateAppointmentDate,
    isSelectedPractitioner,
    mergePractitionerSlotByAllPractitioners.dateItems,
    practitionerId,
    selectedTimeSlot,
    setSelectedTimeSlot,
    updatedAvailableBlocksBySelectedPractitioner,
  ]);

  useEffect(() => {
    updateAvailableBlocksRef(
      practitionerId
        ? updatedAvailableBlocksBySelectedPractitioner
        : doctorWeeklyTimeSlot
    );
  }, [
    updateAvailableBlocksRef,
    updatedAvailableBlocksBySelectedPractitioner,
    practitionerId,
    doctorWeeklyTimeSlot,
  ]);

  const handleChangeDate = (newDate: string) => {
    setDate(newDate);
    setSelectedTimeSlot(null);
  };

  const handleNavigateDayView = (newDate: string) => {
    setDate(newDate);
    setIsDayView(true);
    setSelectedTimeSlot(null);
  };

  const maxDate = moment()
    .clone()
    .add(AMOUNT_DAYS_OF_A_MONTH * 12, 'days')
    .toDate();

  return (
    <>
      <div className="flex mt-1.5 justify-between">
        {isDayView ? (
          <DayViewNavigation date={date} onDateChange={handleChangeDate} />
        ) : (
          <MonthViewNavigation
            date={date}
            setDate={handleChangeDate}
            maxDate={maxDate}
          />
        )}
        <div className="flex gap-x-1">
          <button
            className={`text-14 flex justify-center items-center leading-[2.1rem] border-0 rounded-[0.8rem] font-bold py-[0.45rem] px-[1.8rem] w-[12rem] ${
              !isDayView ? 'shadow-none font-normal' : 'shadow-tooltip'
            }`}
            onClick={() => setIsDayView(true)}
          >
            Day View
          </button>
          <button
            className={`text-14 flex justify-center items-center leading-[2.1rem] border-0 rounded-[0.8rem] font-bold py-[0.45rem] px-[1.8rem] w-[12rem] ${
              isDayView ? 'shadow-none font-normal' : 'shadow-tooltip'
            }`}
            onClick={() => setIsDayView(false)}
          >
            Month View
          </button>
        </div>
      </div>
      <div className="border w-full border-[#CBCBCB] mt-0.2" />
      {isDayView ? (
        isDataLoading ? (
          <div className="w-full flex justify-center mt-2">
            <LoadingSpinner className="all-child:fill-magenta" />
          </div>
        ) : (
          <DayView
            startDateBlock={date}
            setDate={setDate}
            practitionerId={practitionerId}
            serviceId={appointmentDetail.service.id}
            availableBlocks={
              updatedAvailableBlocksBySelectedPractitioner[0]
                ?.availableBlocks || []
            }
            selectedTimeSlot={selectedTimeSlot}
            setSelectedTimeSlot={setSelectedTimeSlot}
            mergePractitionerSlot={mergePractitionerSlotByAllPractitioners}
          />
        )
      ) : (
        <MonthView
          serviceId={appointmentDetail.service.id}
          practitionerId={practitionerId}
          date={date}
          selectedDate={currentDate}
          onNavigateToDayView={handleNavigateDayView}
          timeBlocks={[0]}
          currentDate={currentDate}
          timezone={appointmentDetail.timezone}
          slotInterval={slotInterval}
        />
      )}
    </>
  );
};

export default BookNowPanel;
